import { hasIn, isObject } from 'lodash';
import type { TypeOrArray } from '../../common-interfaces';
import type { JsonDataItem } from '../../generic-types';
import type {
  ValueConverterReturnType,
  VisibilityConditionValueTypes,
} from './converters/target-value/i-target-value-converter';
import { TargetValueConverterFactory } from './converters/target-value/target-value-converter-factory';
import { VisibilityConditionBase } from './visibility-condition-base';
import type { VisibilityCondition, VisibilityConditionOperatorType } from './visibility-condition.interfaces';

export class FieldVisibilityCondition extends VisibilityConditionBase {
  constructor(fieldCondition: VisibilityCondition) {
    super(fieldCondition);
  }

  public runVisibilityCondition(currentData: JsonDataItem): boolean {
    let isHidden = this.defaultIsHidden;

    const targetValueConverter = TargetValueConverterFactory.getConverter(this.fieldCondition.targetValueType);

    if (targetValueConverter && this.fieldCondition.sourceFieldId) {
      if (this.fieldCondition.checkNull === true) {
        const fieldData = currentData[this.fieldCondition.sourceFieldId];
        const isBlank = !fieldData || (Array.isArray(fieldData) && fieldData.length === 0);
        return this.fieldCondition.negation ? isBlank : !isBlank;
      }

      const sourceField = currentData[this.fieldCondition.sourceFieldId];
      if (sourceField || sourceField === false) {
        if (
          this.fieldCondition.targetValueType === 'ENTITY_ID' &&
          Array.isArray(this.fieldCondition.targetValueId) &&
          Array.isArray(sourceField)
        ) {
          const [dataValue, targetValue, negation] = targetValueConverter.convert(
            this.fieldCondition,
            currentData
          ) as ValueConverterReturnType;

          isHidden = this.determineIsHidden(dataValue, targetValue, negation);
        } else if (
          this.fieldCondition.targetValueType === 'STRING' &&
          Array.isArray(this.fieldCondition.targetValueId) &&
          Array.isArray(sourceField)
        ) {
          const [dataValue, targetValue, negation] = targetValueConverter.convert(
            this.fieldCondition,
            currentData
          ) as ValueConverterReturnType;

          isHidden = this.determineIsHidden(dataValue, targetValue, negation);
        } else if (
          this.fieldCondition.targetValueType === 'STRING' &&
          Array.isArray(sourceField) &&
          sourceField.length > 0
        ) {
          const targetValues = targetValueConverter.convert(
            this.fieldCondition,
            currentData
          ) as Array<ValueConverterReturnType>;

          for (let i = 0, length = targetValues.length; i < length; i++) {
            if (targetValues[i] !== undefined) {
              const [dataValue, targetValue, negation] = targetValues[i];
              isHidden = this.determineIsHidden(dataValue, targetValue, negation);
              if ((!negation && !isHidden) || (negation && isHidden)) {
                break;
              }
            }
          }
        } else if (this.fieldCondition.targetValueType === 'DATE') {
          const [dataValue, targetValue, negation, comparator] = targetValueConverter.convert(
            this.fieldCondition,
            currentData
          ) as ValueConverterReturnType;

          isHidden = this.determineIsHidden(dataValue, targetValue, negation, comparator);
        } else {
          const [dataValue, targetValue, negation] = targetValueConverter.convert(
            this.fieldCondition,
            currentData
          ) as ValueConverterReturnType;

          isHidden = this.determineIsHidden(dataValue, targetValue, negation);
        }
      } else {
        if (this.fieldCondition.negation === true) {
          // we will only get here if the condition statement is a "does not equal" and the data is blank, so we will
          // set hidden to false
          isHidden = false;
        }
      }
    } else if (!targetValueConverter && this.fieldCondition.checkNull && this.fieldCondition.sourceFieldId) {
      isHidden = this.checkForBlankField(currentData);
    }

    return isHidden;
  }

  protected override determineIsHidden(
    dataValue?: TypeOrArray<VisibilityConditionValueTypes>,
    visibilityValue?: TypeOrArray<VisibilityConditionValueTypes>,
    flipCheck = false,
    operator: VisibilityConditionOperatorType = 'EQUALS'
  ) {
    let isHidden = this.defaultIsHidden;
    if ('EQUALS' === operator) {
      isHidden = super.determineIsHidden(dataValue, visibilityValue, flipCheck);
    } else if ('GT' === operator) {
      if (dataValue && visibilityValue) {
        isHidden = flipCheck ? dataValue > visibilityValue : dataValue <= visibilityValue;
      } else {
        isHidden = !flipCheck; // when no dataValue then result depends on whether it's a 'contains' or 'not contains' check
      }
    } else if ('LT' === operator) {
      if (dataValue && visibilityValue) {
        isHidden = flipCheck ? dataValue <= visibilityValue : dataValue > visibilityValue;
      } else {
        isHidden = !flipCheck; // when no dataValue then result depends on whether it's a 'contains' or 'not contains' check
      }
    } else if ('CONTAINS' === operator) {
      if (dataValue && typeof dataValue === 'string' && visibilityValue && typeof visibilityValue === 'string') {
        isHidden = flipCheck ? dataValue.indexOf(visibilityValue) === -1 : dataValue.indexOf(visibilityValue) > -1;
      } else {
        isHidden = !flipCheck;
      }
    }

    return isHidden;
  }

  private checkForBlankField(currentData: JsonDataItem) {
    if (this.fieldCondition.sourceFieldId) {
      const fieldData = currentData[this.fieldCondition.sourceFieldId];
      const isBlank =
        !fieldData ||
        (Array.isArray(fieldData) && fieldData.length === 0) ||
        (isObject(fieldData) && hasIn(fieldData, 'value') && !(fieldData as { value: unknown })['value']) ||
        (isObject(fieldData) && hasIn(fieldData, 'noSelectionValue'));
      return this.fieldCondition.negation ? isBlank : !isBlank;
    }

    return this.defaultIsHidden;
  }
}
